import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { queryClient } from '@/lib/queryClient';
import { BASE_PATH, ROUTES } from '@/lib/config';
import { AuthProvider } from '@/auth/AuthProvider';
import { RequireAuth, RequireGuest } from '@/auth/guards';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { AppLayout } from '@/components/layout/AppLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignupPage } from '@/features/auth/SignupPage';
import { ForgotPage } from '@/features/auth/ForgotPage';
import { ResetPage } from '@/features/auth/ResetPage';
import { VerifyPage } from '@/features/auth/VerifyPage';
import { AccountPage } from '@/features/account/AccountPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { ConnectPage } from '@/features/bot/ConnectPage';
import { BotDashboardPage } from '@/features/bot/BotDashboardPage';
import { TradingPage } from '@/features/bot/TradingPage';
import { StrategyPage } from '@/features/bot/StrategyPage';
import { HistoryPage } from '@/features/bot/HistoryPage';
import { BotSettingsPage } from '@/features/bot/BotSettingsPage';
import { AdminPage } from '@/features/admin/AdminPage';

/**
 * App root. Provider order matters: QueryClient → Router → AuthProvider (which
 * reads a query, so it must be inside both). All routes live under the /app
 * basename so the router never sees, or touches, the operator dashboard at /.
 *
 * Routes are eager today (5 tiny screens). When the dashboard/billing/admin
 * arrive in 4A-2/4B, lazy-load those heavier routes with React.lazy — the
 * vendor chunk is already split so only the new feature code downloads.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={BASE_PATH}>
        <AuthProvider>
          <Routes>
            {/* signed-out screens */}
            <Route element={<AuthLayout />}>
              <Route element={<RequireGuest />}>
                <Route path={ROUTES.login} element={<LoginPage />} />
                <Route path={ROUTES.signup} element={<SignupPage />} />
                <Route path={ROUTES.forgot} element={<ForgotPage />} />
              </Route>
              {/* reset + verify are reachable with a token regardless of auth state */}
              <Route path={ROUTES.reset} element={<ResetPage />} />
              <Route path={ROUTES.verify} element={<VerifyPage />} />
            </Route>

            {/* signed-in app */}
            <Route element={<RequireAuth />}>
              <Route element={<AppLayout />}>
                <Route path={ROUTES.account} element={<AccountPage />} />
                <Route path={ROUTES.billing} element={<BillingPage />} />
                <Route path={ROUTES.bot} element={<ConnectPage />} />
                {/* 4B-2: the customer's own bot screens */}
                <Route path={ROUTES.dashboard} element={<BotDashboardPage />} />
                <Route path={ROUTES.trading} element={<TradingPage />} />
                <Route path={ROUTES.strategy} element={<StrategyPage />} />
                <Route path={ROUTES.history} element={<HistoryPage />} />
                <Route path={ROUTES.botSettings} element={<BotSettingsPage />} />
                <Route path={ROUTES.admin} element={<AdminPage />} />
              </Route>
            </Route>

            {/* entry + catch-all */}
            <Route index element={<Navigate to={ROUTES.account} replace />} />
            <Route path="*" element={<Navigate to={ROUTES.login} replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
